// Inbound triage — filter chips (Fig59 / image23): Sector / Min score /
// Status. All three read straight off real AggregatorProject fields
// (vertical, top_match_score, match_run_status) — no invented bucket beyond
// the two honest "unspecified"/"unscored" sentinels for a genuinely missing
// value. Filtering here is presentation-only: it never mutates or drops the
// caller's selection (see AggregatorInbound.tsx — selection persists across
// filter changes by design).
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { AggregatorProject } from '../../types'
import { matchStatusMeta, STATUS_UNSCORED } from './InboundTriageTable'

export const SECTOR_UNSPECIFIED = '__unspecified__'

export interface InboundFilterState {
  sectors: string[]
  minScore: number
  statuses: string[]
}

export const DEFAULT_INBOUND_FILTERS: InboundFilterState = {
  sectors: [],
  minScore: 0,
  statuses: [],
}

const MIN_SCORE_OPTIONS = [
  { value: 0, label: 'Min score: Any' },
  { value: 0.4, label: 'Min score: 40%+' },
  { value: 0.6, label: 'Min score: 60%+' },
  { value: 0.8, label: 'Min score: 80%+' },
]

function toggleValue<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
}

// Exported for a pure, provider-free unit test — the same predicate the page
// uses to derive the visible rows from the real inbound list.
export function applyInboundFilters(rows: AggregatorProject[], filters: InboundFilterState): AggregatorProject[] {
  return rows.filter((p) => {
    if (filters.sectors.length > 0) {
      const sector = p.vertical ?? SECTOR_UNSPECIFIED
      if (!filters.sectors.includes(sector)) return false
    }
    if (filters.minScore > 0) {
      const score = p.top_match_score ?? -1
      if (score < filters.minScore) return false
    }
    if (filters.statuses.length > 0) {
      const status = p.match_run_status ?? STATUS_UNSCORED
      if (!filters.statuses.includes(status)) return false
    }
    return true
  })
}

interface InboundFilterChipsProps {
  /** the full, unfiltered inbound list — drives chip counts so they never shrink as you filter */
  rows: AggregatorProject[]
  filters: InboundFilterState
  onChange: (next: InboundFilterState) => void
  visibleCount: number
  totalCount: number
}

const CHIP_BASE = 'inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors'
const CHIP_ACTIVE = 'bg-iflow-50 text-iflow-700 border-iflow-300'
const CHIP_INACTIVE = 'text-gray-600 border-gray-200 hover:bg-gray-50 bg-white'

export default function InboundFilterChips({ rows, filters, onChange, visibleCount, totalCount }: InboundFilterChipsProps) {
  const [openChip, setOpenChip] = useState<'sector' | 'minScore' | 'status' | null>(null)

  const sectorCounts = new Map<string, number>()
  const statusCounts = new Map<string, number>()
  for (const p of rows) {
    const sector = p.vertical ?? SECTOR_UNSPECIFIED
    sectorCounts.set(sector, (sectorCounts.get(sector) ?? 0) + 1)
    const status = p.match_run_status ?? STATUS_UNSCORED
    statusCounts.set(status, (statusCounts.get(status) ?? 0) + 1)
  }

  const toggleSector = (s: string) => onChange({ ...filters, sectors: toggleValue(filters.sectors, s) })
  const toggleStatus = (s: string) => onChange({ ...filters, statuses: toggleValue(filters.statuses, s) })
  const setMinScore = (v: number) => onChange({ ...filters, minScore: v })
  const clearAll = () => onChange(DEFAULT_INBOUND_FILTERS)

  const activeCount = filters.sectors.length + filters.statuses.length + (filters.minScore > 0 ? 1 : 0)

  const sectorLabel =
    filters.sectors.length === 0
      ? 'Sector'
      : filters.sectors.length === 1
        ? `Sector: ${filters.sectors[0] === SECTOR_UNSPECIFIED ? 'Unspecified' : filters.sectors[0]}`
        : `Sector: ${filters.sectors.length} selected`

  const minScoreLabel = MIN_SCORE_OPTIONS.find((opt) => opt.value === filters.minScore)?.label ?? 'Min score'

  const statusLabel =
    filters.statuses.length === 0
      ? 'Status'
      : filters.statuses.length === 1
        ? `Status: ${matchStatusMeta(filters.statuses[0] === STATUS_UNSCORED ? undefined : filters.statuses[0]).label}`
        : `Status: ${filters.statuses.length} selected`

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="inbound-filter-chips">
      {/* Sector */}
      <div className="relative">
        <button
          type="button"
          data-testid="filter-chip-sector"
          onClick={() => setOpenChip(openChip === 'sector' ? null : 'sector')}
          className={`${CHIP_BASE} ${filters.sectors.length > 0 ? CHIP_ACTIVE : CHIP_INACTIVE}`}
        >
          {sectorLabel}
          {filters.sectors.length > 0 && (
            <span
              role="button"
              aria-label="Clear sector filter"
              data-testid="filter-chip-sector-clear"
              onClick={(e) => {
                e.stopPropagation()
                onChange({ ...filters, sectors: [] })
              }}
              className="ml-0.5"
            >
              ×
            </span>
          )}
          <ChevronDown size={13} aria-hidden />
        </button>
        {openChip === 'sector' && (
          <div
            className="light-card absolute z-20 mt-1 w-56 space-y-0.5 bg-white p-2 shadow-lg"
            data-testid="filter-chip-sector-panel"
          >
            {sectorCounts.size === 0 && <p className="px-2 py-1 text-xs text-gray-400">No sectors yet</p>}
            {[...sectorCounts.entries()].map(([sector, count]) => (
              <label
                key={sector}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
                data-testid={`filter-sector-option-${sector}`}
              >
                <input
                  type="checkbox"
                  checked={filters.sectors.includes(sector)}
                  onChange={() => toggleSector(sector)}
                  className="rounded border-gray-300 text-iflow-500 focus:ring-iflow-400"
                />
                <span className="flex-1">{sector === SECTOR_UNSPECIFIED ? 'Unspecified' : sector}</span>
                <span className="text-xs text-gray-400">({count})</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Min score — same removable-chip pattern as Sector/Status (Fig59), not
          a bare native select, so an active threshold shows its own × clear. */}
      <div className="relative">
        <button
          type="button"
          data-testid="filter-chip-min-score"
          onClick={() => setOpenChip(openChip === 'minScore' ? null : 'minScore')}
          className={`${CHIP_BASE} ${filters.minScore > 0 ? CHIP_ACTIVE : CHIP_INACTIVE}`}
        >
          {minScoreLabel}
          {filters.minScore > 0 && (
            <span
              role="button"
              aria-label="Clear min score filter"
              data-testid="filter-chip-min-score-clear"
              onClick={(e) => {
                e.stopPropagation()
                setMinScore(0)
              }}
              className="ml-0.5"
            >
              ×
            </span>
          )}
          <ChevronDown size={13} aria-hidden />
        </button>
        {openChip === 'minScore' && (
          <div
            className="light-card absolute z-20 mt-1 w-44 space-y-0.5 bg-white p-2 shadow-lg"
            data-testid="filter-chip-min-score-panel"
          >
            {MIN_SCORE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
                data-testid={`filter-min-score-option-${opt.value}`}
              >
                <input
                  type="radio"
                  name="inbound-min-score"
                  checked={filters.minScore === opt.value}
                  onChange={() => setMinScore(opt.value)}
                  className="border-gray-300 text-iflow-500 focus:ring-iflow-400"
                />
                <span className="flex-1">{opt.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Status */}
      <div className="relative">
        <button
          type="button"
          data-testid="filter-chip-status"
          onClick={() => setOpenChip(openChip === 'status' ? null : 'status')}
          className={`${CHIP_BASE} ${filters.statuses.length > 0 ? CHIP_ACTIVE : CHIP_INACTIVE}`}
        >
          {statusLabel}
          {filters.statuses.length > 0 && (
            <span
              role="button"
              aria-label="Clear status filter"
              data-testid="filter-chip-status-clear"
              onClick={(e) => {
                e.stopPropagation()
                onChange({ ...filters, statuses: [] })
              }}
              className="ml-0.5"
            >
              ×
            </span>
          )}
          <ChevronDown size={13} aria-hidden />
        </button>
        {openChip === 'status' && (
          <div
            className="light-card absolute z-20 mt-1 w-56 space-y-0.5 bg-white p-2 shadow-lg"
            data-testid="filter-chip-status-panel"
          >
            {[...statusCounts.entries()].map(([status, count]) => (
              <label
                key={status}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
                data-testid={`filter-status-option-${status}`}
              >
                <input
                  type="checkbox"
                  checked={filters.statuses.includes(status)}
                  onChange={() => toggleStatus(status)}
                  className="rounded border-gray-300 text-iflow-500 focus:ring-iflow-400"
                />
                <span className="flex-1">{matchStatusMeta(status === STATUS_UNSCORED ? undefined : status).label}</span>
                <span className="text-xs text-gray-400">({count})</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {activeCount > 0 && (
        <button
          type="button"
          data-testid="filter-clear-all"
          onClick={clearAll}
          className="text-sm font-medium text-iflow-600 hover:text-iflow-700"
        >
          Clear filters
        </button>
      )}

      <span className="ml-auto text-xs text-gray-500" data-testid="filter-match-count">
        {visibleCount} of {totalCount} inbound match this filter
      </span>
    </div>
  )
}
